import { createContext, useContext, useState, useRef, useCallback, useEffect } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * AUDIO ARCHITECTURE - SINGLETON CONTROLLER
 * 
 * THE 'ONE TRACK' RULE:
 * - Only one audio track can play at a time across the entire app
 * - When a new track starts, all others are automatically stopped
 * - UI state syncs globally (play/pause icons)
 * 
 * NAVIGATION MUTE:
 * - Audio automatically stops on route changes
 * - No ghost music in background
 * 
 * DYNAMIC LINK REFRESH (Feb 21, 2026):
 * - Automatically detects expired Deezer URLs (403/CORS errors)
 * - Fetches fresh URLs from backend and retries playback
 * - Uses backend proxy to bypass CORS on preview subdomain
 */

const API_URL = process.env.REACT_APP_BACKEND_URL;
const AudioContext = createContext(null);

export const AudioProvider = ({ children }) => {
  // Single shared Audio instance
  const audioRef = useRef(new Audio());
  
  // Currently playing track state
  const [currentTrack, setCurrentTrack] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  
  // Track the current route for navigation mute
  const location = useLocation();
  
  /**
   * NAVIGATION MUTE - Stop audio on route change
   */
  useEffect(() => {
    stopAll();
  }, [location.pathname]);
  
  /**
   * STOP ALL - Complete audio halt
   */
  const stopAll = useCallback(() => {
    const audio = audioRef.current;
    if (audio) {
      audio.pause();
      audio.currentTime = 0;
    }
    setCurrentTrack(null);
    setIsPlaying(false);
  }, []);
  
  /**
   * BUILD PROXY URL - Create backend proxy URL for CORS bypass
   */
  const buildProxyUrl = useCallback((originalUrl, eventId) => {
    if (!originalUrl) return null;
    const encodedUrl = encodeURIComponent(originalUrl);
    return `${API_URL}/api/music/proxy?url=${encodedUrl}&event_id=${eventId || 'unknown'}`;
  }, []);
  
  /**
   * REFRESH AUDIO URL - Fetch fresh URL from backend when expired
   */
  const refreshAudioUrl = useCallback(async (eventId) => {
    if (!eventId) return null;
    
    try {
      const response = await fetch(`${API_URL}/api/music/refresh/${eventId}`);
      if (response.ok) {
        const data = await response.json();
        console.log('[Vibe Engine] Refreshed URL for:', eventId, data.track_title);
        return data.preview_url;
      }
    } catch (err) {
      console.log('[Vibe Engine] Refresh failed:', err.message);
    }
    return null;
  }, []);
  
  /**
   * PLAY TRACK - The 'One Track' Rule with Dynamic Link Refresh
   * 
   * @param {string} trackUrl - URL of the audio track
   * @param {string} trackId - Unique identifier (event_id)
   * @param {object} metadata - Event metadata for Sound Profile tracking
   * @param {boolean} useProxy - Force use of backend proxy (default: true)
   * @returns {Promise} - Resolves when playback starts
   */
  const playTrack = useCallback(async (trackUrl, trackId, metadata = {}, useProxy = true) => {
    const audio = audioRef.current;
    
    // If clicking the same track that's playing, toggle pause
    if (currentTrack === trackId && isPlaying) {
      audio.pause();
      setIsPlaying(false);
      return;
    }
    
    // If clicking the same track that's paused, resume
    if (currentTrack === trackId && !isPlaying) {
      try {
        await audio.play();
        setIsPlaying(true);
        console.log('[Vibe Engine] Resumed:', trackId);
      } catch (err) {
        console.log('[Vibe Engine] Resume error:', err);
      }
      return;
    }
    
    // NEW TRACK - Stop any existing audio first (One Track Rule)
    audio.pause();
    audio.currentTime = 0;
    
    // Ensure HTTPS to prevent mixed-content blocks
    let secureUrl = trackUrl;
    if (trackUrl && trackUrl.startsWith('http://')) {
      secureUrl = 'https://' + trackUrl.substring(7);
    }
    
    // Use proxy to bypass CORS on preview subdomain
    const playUrl = useProxy ? buildProxyUrl(secureUrl, trackId) : secureUrl;
    
    console.log('[Vibe Engine] Loading:', metadata.artist || trackId);
    audio.src = playUrl;
    audio.crossOrigin = 'anonymous';
    
    try {
      await audio.play();
      setCurrentTrack(trackId);
      setIsPlaying(true);
      console.log('[Vibe Engine] Playing:', metadata.artist || trackId);
      
      // Track for Sound Profile (if metadata provided)
      if (metadata.event_id) {
        trackPlayForProfile(metadata);
      }
    } catch (err) {
      console.log('[Vibe Engine] Play error:', err.message);
      
      // DYNAMIC LINK REFRESH - Try to get fresh URL if 403/CORS error
      if (err.name === 'NotSupportedError' || err.message.includes('403') || err.message.includes('CORS')) {
        console.log('[Vibe Engine] Attempting Dynamic Link Refresh...');
        
        const eventId = metadata.event_id || trackId;
        const freshUrl = await refreshAudioUrl(eventId);
        
        if (freshUrl) {
          // Retry with fresh URL through proxy
          const freshProxyUrl = buildProxyUrl(freshUrl, eventId);
          audio.src = freshProxyUrl;
          
          try {
            await audio.play();
            setCurrentTrack(trackId);
            setIsPlaying(true);
            console.log('[Vibe Engine] Playing after refresh:', metadata.artist || trackId);
            return;
          } catch (retryErr) {
            console.log('[Vibe Engine] Retry failed:', retryErr.message);
          }
        }
      }
      
      // Specific error handling for common issues
      if (err.name === 'NotAllowedError') {
        console.log('[Vibe Engine] Autoplay blocked - user interaction required');
      }
      
      setCurrentTrack(null);
      setIsPlaying(false);
      throw err;
    }
    
    // Auto-stop handler when track ends
    audio.onended = () => {
      setIsPlaying(false);
      console.log('[Vibe Engine] Track ended');
    };
    
    // Error handler for network issues
    audio.onerror = async () => {
      console.log('[Vibe Engine] Audio error - attempting refresh...');
      const eventId = metadata.event_id || trackId;
      const freshUrl = await refreshAudioUrl(eventId);
      
      if (freshUrl) {
        audio.src = buildProxyUrl(freshUrl, eventId);
        try {
          await audio.play();
          setIsPlaying(true);
        } catch (e) {
          console.log('[Vibe Engine] Recovery failed');
          setIsPlaying(false);
        }
      }
    };
  }, [currentTrack, isPlaying, buildProxyUrl, refreshAudioUrl]);
  
  /**
   * PAUSE CURRENT - Pause without resetting
   */
  const pauseCurrent = useCallback(() => {
    const audio = audioRef.current;
    if (audio && isPlaying) {
      audio.pause();
      setIsPlaying(false);
    }
  }, [isPlaying]);
  
  /**
   * CHECK IF TRACK IS PLAYING
   */
  const isTrackPlaying = useCallback((trackId) => {
    return currentTrack === trackId && isPlaying;
  }, [currentTrack, isPlaying]);
  
  /**
   * SOUND PROFILE TRACKING
   */
  const trackPlayForProfile = async (metadata) => {
    const API_URL = process.env.REACT_APP_BACKEND_URL;
    if (!API_URL) return;
    
    try {
      await fetch(`${API_URL}/api/sound-profile/play`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          event_id: metadata.event_id,
          event_name: metadata.event_name,
          genre: metadata.genre || 'Electronic',
          track_url: metadata.track_url,
          artist: metadata.artist
        })
      });
    } catch (err) {
      console.log('Sound profile tracking error:', err);
    }
  };
  
  const value = {
    playTrack,
    pauseCurrent,
    stopAll,
    isTrackPlaying,
    currentTrack,
    isPlaying
  };
  
  return (
    <AudioContext.Provider value={value}>
      {children}
    </AudioContext.Provider>
  );
};

/**
 * HOOK: useAudio
 * Access the global audio controller from any component
 */
export const useAudio = () => {
  const context = useContext(AudioContext);
  if (!context) {
    throw new Error('useAudio must be used within an AudioProvider');
  }
  return context;
};

export default AudioContext;
